import { getAgeFromDOB } from "../Utils/GetAgeFromDOB";
import { getValue } from "../Utils/GetValue";
import { useCfmContext } from "../CfmContext";
import { useMemo, useEffect } from "react";
import { annualise } from "../Utils/Annualise";
import isEqual from "lodash/isEqual";
import { getYearFromDDMMYYYY } from "../Utils/GetYear";

const money = (v) =>
  typeof v === "number"
    ? v
    : parseFloat(String(v || "").replace(/[^0-9.-]+/g, "")) || 0;

const InvestmentCalculations = () => {
  const { state, dispatch } = useCfmContext();
  const inflationPct = state.inflation ? state.inflation : 0;

  const results = useMemo(() => {
    if (!state?.details?.length || !state?.investments?.length) return [];

    const output = [];

    state.details.forEach((person) => {
      const name = person.name.trim();

      const personInvestments = (state.investments || []).filter(
        (i) => i.name?.trim() === name
      );

      // ---------- DOB-anchored helpers (month math) ----------
      const [dobDD, dobMM, dobYYYY] = String(person.dob || "")
        .split("/")
        .map(Number);
      const dobAnchor = new Date(dobYYYY, (dobMM || 1) - 1, 1); // first of birth month

      const diffMonths = (from, to) =>
        (to.getFullYear() - from.getFullYear()) * 12 +
        (to.getMonth() - from.getMonth());

      // Convert calendar YYYY to month index relative to DOB (Jan of that year)
      const yearToMonthIndex = (yyyy) => {
        const yearNum = parseInt(yyyy, 10);
        if (isNaN(yearNum)) return null;
        const jan = new Date(yearNum, 0, 1);
        return diffMonths(dobAnchor, jan);
      };

      const ageStartIdx = (A) => A * 12;
      const ageEndIdx = (A) => A * 12 + 11;

      // Quick helpers
      const currentAge = getAgeFromDOB(person.dob); // e.g. 36

      personInvestments.forEach((ivstData) => {
        const type = ivstData.type?.trim() || "";

        // --- Parse investment start date and build window starting there
        const parseDDMMYYYY = (s) => {
          const [dd, mm, yyyy] = String(s || "")
            .split("/")
            .map(Number);
          if (!yyyy || !mm) return null;
          return new Date(yyyy, (mm || 1) - 1, 1); // first-of-month
        };

        const startDateObj = parseDDMMYYYY(ivstData.startDate);
        if (!startDateObj) return; // can't simulate without a start date

        // Window: from start month to just before age=101 birthday
        const windowStart = Math.max(0, diffMonths(dobAnchor, startDateObj));
        const windowEnd = 100 * 12 + 11;

        // Age list begins where the window actually has months (and never before current age)
        let startAge = Math.max(currentAge, Math.floor(windowStart / 12));
        const ages = Array.from(
          { length: 101 - startAge },
          (_, i) => startAge + i
        );

        // Map drop risk year -> age (use birth year directly from DOB)
        const birthYear = dobYYYY;
        const dropRiskMap = {};
        (state.dropRiskData || []).forEach(({ year, rate }) => {
          const age = parseInt(year, 10) - birthYear;
          if (!Number.isNaN(age)) dropRiskMap[age] = parseFloat(rate);
        });

        // Funds & inputs
        let investmentValue = annualise({
          value: ivstData.fundValue?.value,
          unit: ivstData.fundValue?.unit || "Annual",
        });

        const annualInvestmentContrib =
          annualise({
            value: ivstData.contribution?.value,
            unit: ivstData.contribution?.unit || "Annual",
          }) || 0;

        const adhocEntries = (state.adHocData || []).filter(
          (a) => a.name?.trim() === name && a.type?.trim() === type
        );
        const adhocContributions = adhocEntries.filter(
          (x) => x.adHocType === "Contribution"
        );
        const adhocWithdrawals = adhocEntries.filter(
          (x) => x.adHocType === "Withdrawal"
        );

        const regularWithdrawalsEntries = (state.regularIncome || []).filter(
          (r) => r.name?.trim() === name && r.type?.trim() === type
        );

        // Contribution window (ages in contStartDate/contEndDate)
        const toInt = (v) => {
          const s = String(v ?? "").trim();
          if (!s) return NaN;
          const n = Number.parseInt(s, 10);
          return Number.isFinite(n) ? n : NaN;
        };
        const resolveContWindow = () => {
          let contStart = toInt(ivstData.contStartDate);
          let contEnd = toInt(ivstData.contEndDate);
          // Sensible defaults if empty
          if (!Number.isFinite(contStart)) contStart = startAge;
          if (!Number.isFinite(contEnd)) contEnd = 100;
          if (contEnd < contStart) contEnd = contStart;
          return { contStart, contEnd };
        };
        const { contStart, contEnd } = resolveContWindow();

        // Helpers for partial first/last age rows (months within [windowStart, windowEnd])
        const monthsActiveInAge = (A) => {
          const s = ageStartIdx(A);
          const e = ageEndIdx(A);
          const lo = Math.max(s, windowStart);
          const hi = Math.min(e, windowEnd);
          return Math.max(0, hi - lo + 1);
        };
        const mfracForAge = (A) => Math.min(1, monthsActiveInAge(A) / 12);

        // Month-overlap helper intersecting age band with window & a given segment
        const overlapMonths = (age, segStart, segEnd) => {
          const aStart = ageStartIdx(age);
          const aEnd = ageEndIdx(age);
          const lo = Math.max(aStart, Math.max(segStart, windowStart));
          const hi = Math.min(aEnd, Math.min(segEnd, windowEnd));
          return Math.max(0, hi - lo + 1);
        };

        // Pending pattern (same as your original)
        let pendingAdhocInvestment = 0;
        let pendingAdhocWithdrawal = 0;

        const fundData = [];

        ages.forEach((age, index) => {
          const yearsFromNow = age - currentAge;
          const inflationFactor = Math.pow(
            1 + inflationPct / 100,
            yearsFromNow
          );
          const usedRate =
            (parseFloat(ivstData.atr) + parseFloat(inflationPct) || 0) / 100;
          const dropRiskPct = dropRiskMap[age];

          // Partial fraction for this age within the window
          const mfrac = mfracForAge(age);
          const isPartial = mfrac > 0 && mfrac < 1;

          // Contribution (age-windowed), prorated if partial
          const contributionBase =
            age >= contStart && age <= contEnd
              ? annualInvestmentContrib * inflationFactor
              : 0;
          const contribution = isPartial
            ? contributionBase * mfrac
            : contributionBase;

          // Regular withdrawals (age-windowed), prorated if partial
          const totalRegularWithdrawalsBase =
            regularWithdrawalsEntries
              .filter((entry) => +entry.start <= age && +entry.end >= age)
              .reduce((sum, entry) => sum + money(entry.amount), 0) *
            inflationFactor;
          const totalRegularWithdrawals = isPartial
            ? totalRegularWithdrawalsBase * mfrac
            : totalRegularWithdrawalsBase;

          // Birthday-year (month-wise) ad-hoc split, also clipped to window
          const appliedAdhocInvestment = adhocContributions.reduce(
            (sum, item) => {
              const mIndex = yearToMonthIndex(item.year);
              if (mIndex == null) return sum;
              const segStart = mIndex; // Jan of that calendar year
              const segEnd = mIndex + 11; // Dec of that calendar year
              const ov = overlapMonths(age, segStart, segEnd);
              if (ov > 0)
                return sum + money(item.amount?.value ?? 0) * (ov / 12);
              return sum;
            },
            0
          );

          const appliedAdhocWithdrawal = adhocWithdrawals.reduce(
            (sum, item) => {
              const mIndex = yearToMonthIndex(item.year);
              if (mIndex == null) return sum;
              const segStart = mIndex;
              const segEnd = mIndex + 11;
              const ov = overlapMonths(age, segStart, segEnd);
              if (ov > 0)
                return sum + money(item.amount?.value ?? 0) * (ov / 12);
              return sum;
            },
            0
          );

          // ---- Flow order (kept consistent with your pattern) ----
          if (index === 0) {
            investmentValue +=
              appliedAdhocInvestment -
              appliedAdhocWithdrawal +
              contribution -
              totalRegularWithdrawals;
          } else {
            investmentValue += pendingAdhocInvestment - pendingAdhocWithdrawal;
            // growth (prorated if partial)
            const g = isPartial ? usedRate * mfrac : usedRate;
            investmentValue *= 1 + g;
            investmentValue += contribution - totalRegularWithdrawals;
          }

          // Next age applies this age's ad-hoc after growth
          pendingAdhocInvestment = appliedAdhocInvestment;
          pendingAdhocWithdrawal = appliedAdhocWithdrawal;

          // Display fund value (post drop risk)
          let fundInvestmentValue =
            investmentValue +
            pendingAdhocInvestment -
            pendingAdhocWithdrawal +
            totalRegularWithdrawals;

          if (dropRiskPct) {
            fundInvestmentValue *= 1 - dropRiskPct / 100;
          }

          fundData.push({
            age,
            atr: `${ivstData.atr}%`,
            dropRiskApplied: dropRiskPct ? `${dropRiskPct}%` : "0%",
            fundValue: `£${Math.max(0, fundInvestmentValue).toFixed(2)}`,
            contribution: `£${Math.max(0, fundInvestmentValue).toFixed(2) > 0 ? contribution.toFixed(2) : 0}`,
            regularWithdrawals: `£${
              fundInvestmentValue < totalRegularWithdrawals
                ? 0
                : totalRegularWithdrawals.toFixed(2)
            }`,
            // show birthday-split values
            adhocInvestment: `£${appliedAdhocInvestment.toFixed(2)}`,
            adhocWithdrawals: `£${appliedAdhocWithdrawal.toFixed(2)}`,
            potentialDropRisk: `${(usedRate * 100).toFixed(2)}%`,
            netFund: `£${investmentValue.toFixed(2)}`,
          });
        });

        output.push({ name, type, investmentFunds: fundData });
      });
    });

    return output;
  }, [state]);

  useEffect(() => {
    const existing = state.investmentResults || [];

    // Person-aware results (ensures empty arrays for people without results)
    const personAwareResults = (state.details || [])
      .map((person) => {
        const personResults = (results || []).filter(
          (r) => r.name.trim() === person.name.trim()
        );
        if (personResults.length === 0) {
          return { name: person.name, type: "", investmentFunds: [] };
        }
        return personResults;
      })
      .flat();

    const isDifferent = !isEqual(existing, personAwareResults);
    if (isDifferent) {
      dispatch({ type: "SET_INVESTMENT_RESULTS", payload: personAwareResults });
    }
  }, [results, state.details, state.investmentResults, dispatch]);

  return results;
};

export default InvestmentCalculations;
